import React, { lazy, memo } from 'react';
import { Card } from '../components/UI/Card';
import { Link } from 'react-router-dom';
export const Blog = () => {
  interface BlogPost {
    id: number;
    title: string;
    excerpt: string;
    content: React.ReactNode;
    author: string;
    date: string;
    image: string;
    slug: string;
  }
  const blogPosts: BlogPost[] = [{
    id: 1,
    title: 'The Art & Science of Relationship Building',
    excerpt: 'When I first started in human capital development, I used to walk into networking events with one mission: meet as many people as possible.',
    content: <div className="space-y-4" data-id="element-2538" data-mp="cxsu9z15eel6">
          <p data-id="element-2539" data-mp="cyu3rz2imt0s">
            When I first started in human capital development, I used to walk
            into networking events with one mission: 👉 meet as many people as
            possible.
          </p>
          <p data-id="element-2540" data-mp="3gkja7lsabgcj">
            I'd leave with a stack of business cards and feel like I was
            winning. But a week later, most of those "connections" barely
            remembered who I was.
          </p>
          <p data-id="element-2541" data-mp="223jbf1ri4t1t">Then one day, something clicked.</p>
          <p data-id="element-2542" data-mp="273f6ucm4vc5r">
            At an event in Abuja, I stopped trying to sell myself and instead
            spent the evening asking people about their stories, their
            challenges, what excited them.
          </p>
          <p data-id="element-2543" data-mp="2fw90xg68og3o">
            One person I spoke with that night became a long-term partner — not
            because I pitched, but because I listened.
          </p>
          <p data-id="element-2544" data-mp="1o52xoqv822a6">That experience taught me the real formula:</p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2545" data-mp="1o7fmnnzedcwq">🔬 The Science</h3>
          <ul className="list-disc pl-6 space-y-2" data-id="element-2546" data-mp="1s5msvow8ivto">
            <li data-id="element-2547" data-mp="1x60y6z6gbro0">
              People are wired to respond to genuine interest (psychology calls
              it reciprocity).
            </li>
            <li data-id="element-2548" data-mp="33ltibumqv5au">
              Familiarity and consistency breed trust — it's why check-ins
              matter more than cold pitches.
            </li>
            <li data-id="element-2549" data-mp="18iwyb0lz8pyp">
              Relationships are built on emotional memory, not transactional
              exchanges.
            </li>
          </ul>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2550" data-mp="14hbmlil6ihq3">🎨 The Art</h3>
          <p data-id="element-2551" data-mp="1igbb8q8oywm9">What I've learned: it's the small, human gestures that matter.</p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-2552" data-mp="1dhrnqleg9crs">
            <li data-id="element-2553" data-mp="1dj2pkfoc4ag9">✅ Remembering a client's daughter just started school.</li>
            <li data-id="element-2554" data-mp="ptgt3zzck9by">✅ Sending a voice note instead of a generic text.</li>
            <li data-id="element-2555" data-mp="ku47tjo4jabh">✅ Following up months later — without an agenda.</li>
          </ul>
          <p data-id="element-2556" data-mp="zii8pp1zcope">
            Those little moments have led to opportunities I could never have
            "strategized" into existence.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2557" data-mp="ur07r0zyio4f">💡 Takeaway</h3>
          <p data-id="element-2558" data-mp="71nxrywt2c12">
            Relationships aren't built at once. They're built brick by brick,
            through curiosity, empathy, and consistency.
          </p>
          <p data-id="element-2559" data-mp="2107xgkbc18f">
            And in a world obsessed with growth hacks and shortcuts, the deepest
            relationships remain the ultimate unfair advantage.
          </p>
          <p className="mt-6 font-medium" data-id="element-2560" data-mp="2iir8g8d9ws5u">
            👉 What's one moment in your career when a simple, human gesture
            turned into a lasting relationship?
          </p>
        </div>,
    author: 'Andrew Igbineweka',
    date: 'August 15, 2025',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/62DL8VzUWoZSsys88qcCmf/speaking_anne.jpg',
    slug: 'art-science-relationship-building'
  }, {
    id: 2,
    title: 'How to Make the Most of Your First Networking Event',
    excerpt: 'Attending your first networking event can be intimidating. Here are five strategies to help you navigate it successfully.',
    content: <div className="space-y-4" data-id="element-2561" data-mp="1c0b5zo8n499g">
          <p data-id="element-2562" data-mp="1c3gv33h9rb56">
            Walking into a room full of strangers can be daunting, especially
            when you're expected to make meaningful connections. But with the
            right mindset and approach, your first networking event can be the
            beginning of valuable professional relationships.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2563" data-mp="1mjrcv8wc13c5">
            1. Set Clear Intentions
          </h3>
          <p data-id="element-2564" data-mp="1iiz38ejjswfc">
            Before the event, decide what you want to achieve. Is it meeting
            three new people in your industry? Learning about a specific topic?
            Having a conversation with a particular speaker? Having clear goals
            helps focus your energy and measure success beyond collecting
            business cards.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2565" data-mp="269uunak3dpm2">
            2. Prepare Your Introduction
          </h3>
          <p data-id="element-2566" data-mp="usv34h5kjet3">
            Craft a concise, engaging introduction that conveys who you are and
            what you're passionate about. Practice until it feels natural, not
            rehearsed. Remember, the goal is conversation, not perfection.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2567" data-mp="31iok9exda7gp">
            3. Ask Thoughtful Questions
          </h3>
          <p data-id="element-2568" data-mp="36e3yi7zae5dn">
            Great networkers are great listeners. Prepare open-ended questions
            that show genuine interest in others. "What are you working on that
            excites you right now?" often yields more interesting conversations
            than "What do you do?"
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2569" data-mp="3k9pks7e4yb2d">
            4. Find the Connectors
          </h3>
          <p data-id="element-2570" data-mp="3p8smpiytk164">
            In every room, there are people who know everyone. These connectors
            can introduce you to others who align with your interests or goals.
            How to spot them? They're often greeting multiple people or being
            approached frequently.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2571" data-mp="1zu4dtrpft1cu">
            5. Follow Up Meaningfully
          </h3>
          <p data-id="element-2572" data-mp="37495r01dbz9n">
            Within 48 hours, reach out to people you connected with. Reference
            something specific from your conversation and suggest a clear next
            step if appropriate. A thoughtful follow-up is what transforms a
            brief meeting into a potential relationship.
          </p>
          <p className="mt-6 font-medium" data-id="element-2573" data-mp="3c2u7j3w9qr07">
            Remember, meaningful networking isn't about quantity—it's about
            quality. One authentic connection can be more valuable than a dozen
            surface-level interactions.
          </p>
        </div>,
    author: 'The Networking Salon Team',
    date: 'July 22, 2025',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gumJfCcrUgAhRz8naofitq/Network_tobi.jpg',
    slug: 'make-most-first-networking-event'
  }, {
    id: 3,
    title: 'Building Community in a Digital-First World',
    excerpt: 'As remote work becomes the norm, how do we maintain authentic human connections?',
    content: <div className="space-y-4" data-id="element-2574" data-mp="ranqmy3ibg2u">
          <p data-id="element-2575" data-mp="rbqh54wx3gi8">
            The shift to digital-first work environments has fundamentally
            changed how we build and maintain professional relationships. While
            technology has removed geographical barriers, it has created new
            challenges for fostering genuine community.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2576" data-mp="mdp34yecm7dm">The Digital Paradox</h3>
          <p data-id="element-2577" data-mp="8j4r518oyk5k">
            We're more connected than ever through digital tools, yet many
            professionals report feeling increasingly isolated. This paradox
            exists because while information exchange has become easier,
            emotional connection requires more intentional effort in virtual
            spaces.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2578" data-mp="3lqfznen2ilvg">
            Creating Digital Rituals
          </h3>
          <p data-id="element-2579" data-mp="3qqc239g4a58w">
            Strong communities are built on shared rituals. In a digital
            environment, these might include:
          </p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-2580" data-mp="a0lkw4ohoual">
            <li data-id="element-2581" data-mp="53bog52onid9">
              Weekly virtual coffee chats with no agenda beyond connection
            </li>
            <li data-id="element-2582" data-mp="3gxk1iwqss7">
              Monthly skill-sharing sessions where team members teach something
              they're passionate about
            </li>
            <li data-id="element-2583" data-mp="3sanj0c6bceb1">
              Quarterly virtual retreats focused on relationship-building
              alongside strategic planning
            </li>
          </ul>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2584" data-mp="3igww3bpu4bxg">
            The Power of Vulnerability
          </h3>
          <p data-id="element-2585" data-mp="3djo8uwptxhpc">
            Digital spaces can feel impersonal, making vulnerability even more
            powerful. Leaders who share challenges, ask for help, and
            acknowledge uncertainty create psychological safety that allows
            communities to deepen.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2586" data-mp="2zk5gbc9i4ey5">
            Hybrid Connection Models
          </h3>
          <p data-id="element-2587" data-mp="1f1bd9xespqfs">
            The most resilient communities blend digital and in-person
            experiences. Consider:
          </p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-2588" data-mp="1o29hogt5bzyw">
            <li data-id="element-2589" data-mp="1o2sqn80tje0s">
              Using digital tools for regular touchpoints and information
              sharing
            </li>
            <li data-id="element-2590" data-mp="1syzzb8kln3mf">
              Reserving in-person gatherings for deeper relationship building
              and complex collaboration
            </li>
            <li data-id="element-2591" data-mp="10dqa63hx7r7v">
              Creating "digital campfires" – smaller, more intimate online
              gatherings that foster closer connections
            </li>
          </ul>
          <p className="mt-6 font-medium" data-id="element-2592" data-mp="1a31j9q4t4vvr">
            The future belongs to organizations and leaders who can thoughtfully
            design community experiences that leverage both digital convenience
            and human connection. The goal isn't to replicate in-person
            experiences online, but to create new models of community that
            embrace the unique advantages of both worlds.
          </p>
        </div>,
    author: 'The Networking Salon Team',
    date: 'June 5, 2025',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9ciCFjhcNKhhvWPuNF4wxL/Network_Scoot.jpg',
    slug: 'building-community-digital-first-world'
  }];
  return <div className="pt-20" data-id="element-2593" data-mp="ilbthxqdua54">
      <section className="py-16 bg-secondary/5" data-id="element-2594" data-mp="im4roab9zrbn">
        <div className="container mx-auto px-4" data-id="element-2595" data-mp="njwmpd77vtk2">
          <h1 className="text-4xl font-bold text-center text-secondary-dark mb-6" data-id="element-2596" data-mp="nkoeecxp1752">
            Blog
          </h1>
          <p className="text-center text-lg text-secondary max-w-3xl mx-auto" data-id="element-2597" data-mp="shopj29eh031">
            Insights, stories, and practical advice on networking, community
            building, and professional growth.
          </p>
        </div>
      </section>
      <section className="py-16" data-id="element-2598" data-mp="4svo4qa0e011">
        <div className="container mx-auto px-4" data-id="element-2599" data-mp="4s41i4ye3cmw">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2600" data-mp="3tfa47id18h80">
            {blogPosts.map(post => <Link to={`/blog/${post.slug}`} key={post.id} className="block" data-id="element-2601" data-mp="3ofxkv7lddsa6">
                <Card className="flex flex-col cursor-pointer hover:shadow-md transition-shadow h-full" data-id="element-2602" data-mp="3jgudvhltxbzk">
                  <div className="aspect-[16/9] relative overflow-hidden rounded-t-lg" data-id="element-2603" data-mp="3ei9m86kn80mq">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover" loading="lazy" data-id="element-2604" data-mp="3agfu4moik4y6" />
                  </div>
                  <div className="p-6 flex-grow flex flex-col" data-id="element-2605" data-mp="30redqve23ljo">
                    <h2 className="text-xl font-bold text-secondary-dark mb-2" data-id="element-2606" data-mp="2vs1qi9j9wcgk">
                      {post.title}
                    </h2>
                    <p className="text-secondary mb-4 flex-grow" data-id="element-2607" data-mp="2qrx98hajjdpa">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-200" data-id="element-2608" data-mp="2mt7m3wzpahsg">
                      <span className="text-sm text-secondary" data-id="element-2609" data-mp="2h4t9gs3a9cwo">
                        {post.author}
                      </span>
                      <span className="text-sm text-secondary" data-id="element-2610" data-mp="235tn5r38ta3o">
                        {post.date}
                      </span>
                    </div>
                  </div>
                </Card>
              </Link>)}
          </div>
        </div>
      </section>
    </div>;
};